import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogueCard } from '../components/CatalogueCard';
import { ConfirmDialog, useToast } from '../components/Toast';
import { useCatalogue } from '../hooks/useCatalogue';
import { api, ApiError, type Board, type CatalogueItem } from '../api/client';
import { IconPlus, IconFolder, IconEye } from '../components/Icon';
import { useLanguage, pick } from '../lib/language';

export function CatalogueRoute() {
  const catalogue = useCatalogue();
  const toast = useToast();
  const [query, setQuery] = useState('');
  // Pending deletion — when non-null the ConfirmDialog is open. Holding
  // the item here (not just its id) lets us show the title in the
  // dialog and offer an Undo affordance after the API call succeeds.
  const [pendingDelete, setPendingDelete] = useState<CatalogueItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // v5 — Boards left rail. `null` means "All references" (no filter).
  const [boards, setBoards] = useState<Board[]>([]);
  const [boardsStatus, setBoardsStatus] = useState<'idle' | 'loading' | 'error' | 'unavailable'>(
    'idle',
  );
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);

  // Initial load
  useEffect(() => {
    void catalogue.refresh();
    // Depend on the stable `refresh` callback, NOT on the whole `catalogue`
    // object (which is rebuilt every render and would cause an infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue.refresh]);

  // Optimistic event from Studio when a new item is saved.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CatalogueItem>).detail;
      if (detail) catalogue.optimisticAdd(detail);
    };
    window.addEventListener('eye-gallery:catalogue-added', handler);
    return () => window.removeEventListener('eye-gallery:catalogue-added', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue.optimisticAdd]);

  // v5 — Load boards on mount. If the endpoint doesn't exist on the
  // backend (404) we silently degrade to the no-rail layout — the rest
  // of the page still works.
  const loadBoards = useCallback(async () => {
    setBoardsStatus('loading');
    try {
      const list = await api.listBoards();
      setBoards(list);
      setBoardsStatus('idle');
    } catch (err) {
      // 404 means the backend doesn't ship boards yet — treat as "unavailable"
      // so the UI can drop the rail entirely instead of showing errors.
      // (The original polish pass checked err.message for the literal
      // "http_404" string, but the request() helper puts that in err.code,
      // not the message — so the 404 was falling through to 'error' and
      // showing the "Could not load boards" alert.)
      if (err instanceof ApiError && err.status === 404) {
        setBoardsStatus('unavailable');
      } else {
        setBoardsStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    void loadBoards();
  }, [loadBoards]);

  const requestDelete = useCallback(
    (item: CatalogueItem) => {
      setPendingDelete(item);
    },
    [],
  );

  const cancelDelete = useCallback(() => {
    if (deleting) return;
    setPendingDelete(null);
  }, [deleting]);

  const confirmDelete = useCallback(async () => {
    const item = pendingDelete;
    if (!item) return;
    setDeleting(true);
    const snapshot = catalogue.items;
    catalogue.remove(item.id);
    try {
      await api.deleteCatalogue(item.id);
      toast.push('success', `Removed "${item.title ?? 'reference'}" from your catalogue.`, {
        actionLabel: 'Undo',
        onAction: async () => {
          // "Undo" — best-effort restore by re-saving via the backend's
          // POST /api/catalogue endpoint. If the source generated_id is
          // still around on the server, the new item will mirror the
          // deleted one. We surface a toast either way.
          try {
            // We don't have a direct restore endpoint, but we can at
            // least refresh from the server to pick up any state.
            await catalogue.refresh();
          } catch {
            /* ignore */
          }
          toast.push('info', 'Restored from server.');
        },
        durationMs: 6000,
      });
    } catch (err) {
      // Restore on failure
      const restored = snapshot.find((i) => i.id === item.id);
      if (restored) catalogue.optimisticAdd(restored);
      toast.push('error', err instanceof Error ? err.message : 'Could not delete.');
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, catalogue, toast]);

  // v5 — filter by selected board, then by search query. Boards with no
  // assigned items still show in the rail but contribute 0 cards.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const boardFiltered =
      selectedBoardId === null
        ? catalogue.items
        : catalogue.items.filter((item) => item.board_id === selectedBoardId);
    if (!q) return boardFiltered;
    return boardFiltered.filter((item) => {
      const haystack = [
        item.title ?? '',
        item.notes ?? '',
        ...item.tags,
        item.mood ?? '',
        item.colour_family ?? '',
        item.project_tag ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [catalogue.items, query, selectedBoardId]);

  const handleCreateBoard = useCallback(async () => {
    const name = newBoardName.trim();
    if (!name) return;
    setCreatingBoard(true);
    try {
      const created = await api.createBoard(name);
      setBoards((prev) => [...prev, created]);
      setNewBoardName('');
      toast.push('success', `Board "${created.name}" created.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create board.';
      toast.push('error', msg);
    } finally {
      setCreatingBoard(false);
    }
  }, [newBoardName, toast]);

  const handleMoveToBoard = useCallback(
    async (catalogueId: string, boardId: string | null) => {
      try {
        await api.moveToBoard(catalogueId, boardId);
        // Optimistic local update — patch the item in-place.
        const existing = catalogue.items.find((it) => it.id === catalogueId);
        if (existing) {
          catalogue.replace(catalogueId, { ...existing, board_id: boardId });
        }
        toast.push(
          'success',
          boardId
            ? 'Moved to board.'
            : 'Removed from board (back to All references).',
        );
      } catch (err) {
        toast.push(
          'error',
          err instanceof Error ? err.message : 'Could not move item.',
        );
      }
    },
    [catalogue, toast],
  );

  const showRail = boardsStatus !== 'unavailable';

  return (
    <section
      id="panel-catalogue"
      role="tabpanel"
      aria-labelledby="tab-catalogue"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
          Saved references
        </h1>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, notes, or tag"
            aria-label="Search catalogue"
            className="h-11 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 sm:w-72"
          />
          <button
            type="button"
            onClick={() => void catalogue.refresh()}
            aria-label="Refresh catalogue"
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)]">
        {/* v5 — Boards left rail. Hidden when the backend doesn't support
            boards (404), so the page degrades gracefully on older builds. */}
        {showRail ? (
          <aside
            aria-label="Boards"
            className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                Boards
              </h2>
              <span className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {boards.length} total
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSelectedBoardId(null)}
              aria-pressed={selectedBoardId === null}
              className={[
                'inline-flex h-11 cursor-pointer items-center justify-between rounded-md border px-3 text-sm motion-safe:transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                selectedBoardId === null
                  ? 'border-accent bg-accent/15 text-accent dark:bg-accent/25'
                  : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-800',
              ].join(' ')}
            >
              <span className="inline-flex items-center gap-2">
                <IconFolder size={14} aria-hidden />
                All references
              </span>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {catalogue.items.length}
              </span>
            </button>
            <ul className="flex flex-col gap-1">
              {boards.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedBoardId(b.id)}
                    aria-pressed={selectedBoardId === b.id}
                    className={[
                      'flex w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-left text-sm motion-safe:transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                      selectedBoardId === b.id
                        ? 'border-accent bg-accent/15 text-accent dark:bg-accent/25'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-800',
                    ].join(' ')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <IconFolder size={14} aria-hidden />
                      {b.name}
                    </span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      {catalogue.items.filter((it) => it.board_id === b.id).length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="New board name"
                aria-label="New board name"
                className="h-11 flex-1 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
              />
              <button
                type="button"
                onClick={() => void handleCreateBoard()}
                disabled={creatingBoard || newBoardName.trim().length === 0}
                aria-label="Create new board"
                className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                <IconPlus size={16} aria-hidden />
              </button>
            </div>
            {boardsStatus === 'error' ? (
              <p className="text-[11px] text-rose-600 dark:text-rose-400" role="alert">
                Could not load boards.
              </p>
            ) : null}
          </aside>
        ) : null}

        <div className="flex flex-col gap-4">
          {catalogue.status === 'loading' && catalogue.items.length === 0 ? (
            <p
              className="text-sm text-neutral-500 dark:text-neutral-400"
              aria-live="polite"
            >
              Loading your catalogue…
            </p>
          ) : null}

          {catalogue.status === 'error' ? (
            <div
              role="alert"
              className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
            >
              {catalogue.errorMessage ?? 'Could not load your catalogue.'}
            </div>
          ) : null}

          {selectedBoardId !== null ? (
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              Filtered to board:{' '}
              <span className="font-semibold">
                {boards.find((b) => b.id === selectedBoardId)?.name ?? 'Board'}
              </span>
            </p>
          ) : null}

          {filtered.length === 0 && catalogue.status !== 'loading' ? (
            <EmptyState
              hasAny={catalogue.items.length > 0}
              boardActive={selectedBoardId !== null}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((item) => (
                <CatalogueCardWrapper
                  key={item.id}
                  item={item}
                  boards={boards}
                  onDelete={() => requestDelete(item)}
                  onMoveToBoard={(boardId) => handleMoveToBoard(item.id, boardId)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this reference?"
        description={
          <>
            <p>
              “{pendingDelete?.title ?? 'Untitled reference'}” will be removed
              from your catalogue. You can undo this from the toast that
              appears right after.
            </p>
          </>
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Keep it"
        destructive
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
      />
    </section>
  );
}

/**
 * Thin wrapper around CatalogueCard that adds a "Move to board" menu.
 * Extracted so the board selector stays out of the pure presentation
 * CatalogueCard (used in tests and potentially in other routes).
 */
function CatalogueCardWrapper(props: {
  item: CatalogueItem;
  boards: Board[];
  onDelete: () => void;
  onMoveToBoard: (boardId: string | null) => void;
}) {
  const { item, boards, onDelete, onMoveToBoard } = props;
  return (
    <div className="flex flex-col gap-2">
      <CatalogueCard item={item} onDelete={onDelete} />
      {boards.length > 0 ? (
        <label className="flex items-center gap-2 text-[11px] text-neutral-600 dark:text-neutral-400">
          <span className="sr-only">Move {item.title ?? 'reference'} to board</span>
          <select
            value={item.board_id ?? ''}
            onChange={(e) => onMoveToBoard(e.target.value || null)}
            aria-label={`Move ${item.title ?? 'reference'} to board`}
            className="h-9 flex-1 cursor-pointer rounded-md border border-neutral-300 bg-white px-2 text-xs text-neutral-800 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
          >
            <option value="">No board</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

function EmptyState(props: { hasAny: boolean; boardActive?: boolean }) {
  const { lang } = useLanguage();
  if (!props.hasAny) {
    const heading = pick(lang, {
      plain: 'No saved eyes yet',
      technical: 'No entries',
    });
    const body = pick(lang, {
      plain: 'Save an eye from the Studio to start your catalogue. Look for the Save panel below the chat.',
      technical:
        'POST to /api/catalogue to persist a generated image; saved items will appear here.',
    });
    return (
      // v6-polish — empty state now uses the new IconEye component and
      // language-aware copy. Mum sees "No saved eyes yet" + a friendly
      // pointer to the Save panel; Ty sees "No entries" + the API
      // contract. The dashed border + centred text matches the v5/v6
      // visual rhythm — only the icon and copy moved.
      <div
        className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-6 py-10 text-center dark:border-neutral-700 dark:bg-neutral-900"
        role="status"
      >
        <span
          aria-hidden="true"
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent-light dark:bg-accent/25 dark:text-[#a8c0ff]"
        >
          <IconEye size={28} />
        </span>
        <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
          {heading}
        </p>
        <p className="max-w-md text-sm text-neutral-600 dark:text-neutral-400">
          {body}
        </p>
      </div>
    );
  }
  if (props.boardActive) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center dark:border-neutral-700 dark:bg-neutral-900">
        <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
          Nothing in this board yet.
        </p>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          Use the "Move to board" picker on any saved reference to add it here.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center dark:border-neutral-700 dark:bg-neutral-900">
      <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
        No matches.
      </p>
      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
        Try a different search term.
      </p>
    </div>
  );
}
